import React, { useRef } from "react";
import { Calculator } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import Seo from "../Seo.jsx";
import Tooltip from "./Tooltip";
import ScrollModal from "../ui/ScrollModal.jsx";

import "./guides.css";
import guidesConfig from "../../config/guidesConfig";
import bb2 from './images/bbexplainers/2.png'
import bb3 from './images/bbexplainers/3.png'
import bb4 from './images/bbexplainers/4.png'

import bb5 from './images/bbexplainers/5.png'

import av from './images/bbexplainers/av.png'

import cs from './images/bbexplainers/cs.png'
import smlist from './images/sm-bb-list.png'
import wh from './images/bb-av-wh.png'
import smav from './images/bb-av-cssm.png'


const LayBetBuildersGuide = () => {
  const meta = guidesConfig.layBetBuildersGuide?.seo || {};
  const scrollRef = useRef(null);

  return (
    <div className="guides-content">
      <Seo
        title={meta.title}
        description={meta.description}
        url={guidesConfig.layBetBuildersGuide?.route || "/guides"}
      />

      <p>
      Laying bet builder bets has become a key skill for matched bettors.
      </p>
<p>They're very profitable for bookies, so naturally, there are plenty of bet builder offers available to profit from.</p>

      <h2>What Is a Bet Builder?</h2>
      <h6 className="info">Bet builders let punters combine different, unrelated bets in one bet.</h6>
      <div className="example-container">
        <h4>EXAMPLES</h4>
      <blockquote>Chelsea to win, Tottenham to get the most cards</blockquote>
      <blockquote>Salah to score, Liverpool to have the most corners, both teams to score</blockquote>
      <blockquote>Celtic to have 5+ shots on target, over 2.5 total goals, Celtic clean sheet</blockquote>
      </div>

      <h6 className="warn">Bet builders can't be layed directly. You cannot build the matching bet at an exchange, and lay that like an accumulator. But as matched bettors, we have tricks to match certain combinations of selections at exchanges.</h6>
    <p>So, in this post, we're going to explain exactly how to go about laying bet builders – specifically football bet builders, though similar principles can sometimes be applied to other markets.</p>


      

      <h2>So if you can't lay directly, what can you do?</h2>
        <p>To lay bet football bet builders, we are going to use a trick.</p>
        <h6 className="info">We're going to create a bet builder which is <em>equivalent</em> to a market that exists on exchanges.</h6>

        <div className="example-container">
        <h4>Example</h4>
            

            <div className="grid-fixed g2 mb-32">
                <div className="col">
                    <img src={av} alt="Lay bet builder on Matchbook" />
                </div>
                <div className="col">
            <p>Let's say we have an offer from William Hill. They're offering <strong>£10 in free bets when we bet £10 on a 2+ leg bet builder.</strong></p>
                <p>We need to think of a combination of bets which is going to be equivalent to a single exchange market.</p>
                <blockquote><strong>Home win</strong> combined with <strong>Total Goals – Under 1.5</strong> is logically the same as <strong>1:0 Correct Score</strong></blockquote>
            </div>
            
            </div>
            <p>So we decide to create the following bet builder on Aston Villa v. West Ham:</p>
            <img src={wh} alt="William hill bet builder" className="mb-32" />
            <p>We <span className="back">back</span> the bet for £10 at William Hill at odds 9.5</p>
            <p>Using the calculator, we check the right lay stake, then we find the Correct Score market on Smarkets:</p>
            <p>We <span className="lay">lay</span> Correct Score 1:0 on Smarkets.</p>
            <img src={smav} alt="Smarkets 0% commission" />

            </div>
            <CTALarge
        to="/lay-stake-calculator"
        text="Go to Lay Stake Calculator"
        icon={Calculator}
      />
        <h2>Bookies won't always allow every combo</h2>
        <p>Some bookies prevent us creating bet builders which can be layed. They know this is what a matched bettor would do.</p>
        <p>They do this by refusing certain markets to be combined, such as <span className="key">match result</span> and <span className="key">correct score</span>.</p>
        <p>Over time, you'll get a feel for what is allowed at each bookie and which combos work best.</p>
       
        <h2>The markets we can use to lay bet builders</h2>
        <h6 className="info">Remember, we can't combine markets and lay them at exchanges. We have to find a single market that is <em>equivalent</em> to our bet builder.</h6>
        <p>There are 8 main exchange markets matched bettors use to lay bet builders. They have clear bet builder equivalent markets and often decent <Tooltip content="How much money is available for a market to match your bet">liquidity</Tooltip>. Here they are on Smarkets:</p>
        <img src={smlist} alt="Bet builder markets on Smarkets" />


        <h2>Laying on the Exchange Market <em>Half Time / Full Time</em></h2>
      <div className="grid-fixed g2">
        <div className="col">
            <img src={bb5} alt="Lay bet builder on Matchbook" />
        </div>
        <div className="col">
            <p>This market lets punters bet on the correct outcome at half-time and full-time, combined. For example, the outcome <span className="key">Home/Home</span> means the home team were winning at both half-time and full-time.</p>
        <h6 className="info">This market is called <em>Half-time/Full-time</em> on all of the exchanges.</h6>
    </div>
    </div>
            <p>The possible outcomes are <span className="key">Home/Home</span>, <span className="key">Home/Draw</span>, <span className="key">Home/Away</span>, <span className="key">Draw/Home</span>, <span className="key">Draw/Draw</span>, <span className="key">Draw/Away</span>, <span className="key">Away/Home</span>, <span className="key">Away/Draw</span>, <span className="key">Away/Away</span>.</p>
    <h6 className="info">Many combinations are possible, often with BTTS, Goals Over/Under or Team Goals Over/Under.</h6>

<table>
  <thead>
    <tr>
      <th>Bet Builder Examples (Back)</th>
      <th>Lay Market</th>
    </tr>
  </thead>
  <tbody>
  <tr><td>2 Legs: Result <span className="and">AND</span> Half-Time Result</td><td>HT/FT: Result/Result</td></tr>
    <tr>
      <td>3 Legs: Draw <span className="and">AND</span> Team to Win Half Time Result <span className="and">AND</span> Over 1.5 Goals</td>
      <td>HT/FT: Draw/Draw</td>
    </tr>
    <tr>
      <td>4 Legs: Draw <span className="and">AND</span> Team to Win Half Time Result <span className="and">AND</span> Over 1.5 Goals <span className="and">AND</span> Over 0.5 Team Goals</td>
      <td>HT/FT: Draw/Draw</td>
    </tr>
    <tr>
      <td>5 Legs: Team to Win <span className="and">AND</span> Team to Win Half Time Result <span className="and">AND</span> Over 0.5 Goals <span className="and">AND</span> Over 0.5 Team Goals <span className="and">AND</span> Over 0.5 First Half Goals</td>
      <td>HT/FT: Team A/Team A</td>
    </tr>
  </tbody>
</table>

<h2>Laying on the Exchange Market <em>Winner and Both Teams To Score
</em></h2>
<h6 className="info">"BTTS" means Both Teams to Score</h6>

<div className="grid-fixed g2">
    <div className="col">
        <img src={bb2} alt="Lay bet builder on Smarkets" />
    </div>
    <div className="col">
        <p>This is one of the most straightforward options.</p>
        <p>The market lets punters bet on the outcome and whether both teams scored, combined. It has six possible outcomes: <span className="key">Home / Yes</span>, <span className="key">Home / No</span>, <span className="key">Draw / Yes</span>, <span className="key">Draw / No</span>, <span className="key">Away / Yes</span>, <span className="key">Away / No</span>.</p>
        <h6 className="info">This market is called <em>Winner and both teams to score</em> on Smarkets, <em>Match Result and Both Teams To Score</em> on Matchbook, <em>1x2 & both teams to score</em> on Betdaq and <em>Match Odds and Both teams to Score</em> on Betfair.</h6>

    </div>
</div>

<h3>2 leg options</h3>
<table>
  <thead>
    <tr>
      <th>Bet Builder Examples (Back)</th>
      <th>Lay Market</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Win <span className="and">AND</span> BTTS Yes</td>
      <td>Winner/Yes</td>
    </tr>
    <tr>
      <td>Win <span className="and">AND</span> BTTS No</td>
      <td>Winner/No</td>
    </tr>
  </tbody>
</table>
<h3>3+ legs</h3>
<table>
  <thead>
    <tr>
      <th>Bet Builder Examples (Back)</th>
      <th>Lay Market</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>3: Team to Win <span className="and">AND</span> BTTS <span className="and">AND</span> Over 2.5 Goals</td>
      <td>Winner/Yes</td>
    </tr>
    <tr>
      <td>4: Team to Win <span className="and">AND</span> Over 0.5 Goals <span className="and">AND</span> Over 0.5 Team Goals <span className="and">AND</span> BTTS - Yes</td>
      <td>Winner/Yes</td>
    </tr>
    <tr>
      <td>5: Team to Win <span className="and">AND</span> Over 0.5 Goals <span className="and">AND</span> Over 0.5 Team A Goals <span className="and">AND</span> Over 0.5 Team B Goals <span className="and">AND</span> BTTS</td>
      <td>Winner/Yes</td>
    </tr>
  </tbody>
</table>



      <h2>Laying on the Exchange Market <em>Winner and Over/Under Goals</em></h2>

      <div className="grid-fixed g2">
        <div className="col">
            <img src={bb3} alt="Lay bet builder on Matchbook" />
        </div>
        <div className="col">
        <p>The market lets punters bet on the outcome and whether over or under N goals were scored, combined. For each score from 0.5 to 4.5, it has six possible outcomes: <span className="key">Home / Yes</span>, <span className="key">Home / No</span>, <span className="key">Draw / Yes</span>, <span className="key">Draw / No</span>, <span className="key">Away / Yes</span>, <span className="key">Away / No</span>.</p>
        <h6 className="info">These markets are called <em>Winner and over</em> on Smarkets, <em>Match Result and Total Goals</em> on Matchbook, <em>1x2 & total</em> on Betdaq and <em>Match Odds and Over/Under</em> on Betfair.</h6>
        </div>
    </div>
     
<table>
  <thead>
    <tr>
      <th>Bet Builder Examples (Back)</th>
      <th>Lay Market</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Win <span className="and">AND</span> Over X Goals</td>
      <td>Winner &amp; Over/Under X Goals</td>
    </tr>
  </tbody>
</table>






<h2>Laying on the Exchange Market <em>Correct Score</em></h2>

<h6 className="info">This market is called <em>Correct Score</em> on all exchanges</h6>

<p>Lots of potential bet builder combos are equivalent to a correct score, and this makes it a great approach to become familiar with.</p>
<div className="example-container">
<div className="grid-fixed g2">
<div className="col">
<img src={cs} alt="How to lay a bet builder correct score" />
</div>
<div className="col">
<p>Imagine Liverpool are playing Newcastle:</p>
    <h4>Example</h4>
    <blockquote>
    <em>Scenario:</em> Both Liverpool and Newcastle score, but there are 2 or fewer goals in the match.
    </blockquote>
    <p>We want to come up with a bet builder where <strong>only one final score is possible</strong></p>

</div>

</div>
    <p>
<strong>What we <span className="back">back</span>:</strong> Total goals: Under 2.5 AND Both teams to score: Yes</p>
<p>
        <strong>What we <span className="lay">lay</span>:</strong> Correct Score: 2:1

</p>
        
</div>




<h3>2 leg options</h3>
<table>
<thead>
<tr>
<th>Bet Builder Examples (Back)</th>
<th>Lay Market</th>
</tr>
</thead>
<tbody>
<tr>
<td>
Draw <span className="and">AND</span> Correct Score 0-0<br></br>
Draw <span className="and">AND</span> BTTS No<br></br>
Team A Under 0.5 <span className="and">AND</span> Team B Under 0.5<br></br>
Draw <span className="and">AND</span> Under 1.5 Goals
</td>
<td>0-0</td>
</tr>
<tr>
<td>
Win <span className="and">AND</span> Correct Score 1-0<br></br>
Win <span className="and">AND</span> Under 1.5 Goals
</td>
<td>1-0</td>
</tr>
<tr>
<td>
Draw <span className="and">AND</span> Correct Score 1-1<br></br>
BTTS <span className="and">AND</span> Under 2.5 Goals
</td>
<td>1-1</td>
</tr>
<tr>
<td>
Win <span className="and">AND</span> Correct Score 2-1<br></br>
Win <span className="and">AND</span> BTTS
</td>
<td>2-1</td>
</tr>
</tbody>
</table>
<h3>3 leg options</h3>
<h6 className="info">Many combinations are possible. We're listing one for each Correct Score market.</h6>
<table>
<thead>
<tr>
<th>Bet Builder Examples (Back)</th>
<th>Lay Market</th>
</tr>
</thead>
<tbody>
<tr>
<td>Team A Under 0.5 Goals <span className="and">AND</span> Team B Under 0.5 Goals <span className="and">AND</span> BTTS-No</td>
<td>0-0</td>
</tr>
<tr>
<td>Win <span className="and">AND</span> BTTS - No <span className="and">AND</span> Under 1.5 Goals</td>
<td>1-0</td>
</tr>
<tr>
<td>Draw <span className="and">AND</span> BTTS <span className="and">AND</span> Under 2.5 Goals</td>
<td>1-1</td>
</tr>
<tr>
<td>Win <span className="and">AND</span> BTTS - No <span className="and">AND</span> Correct Score 2-0</td>
<td>2-0</td>
</tr>
<tr>
<td>Win <span className="and">AND</span> BTTS <span className="and">AND</span> Exactly 3 / Under 3.5 / Under 4.5 Goals</td>
<td>2-1</td>
</tr>
<tr>
<td>Win <span className="and">AND</span> BTTS - No <span className="and">AND</span> Correct Score 3-0</td>
<td>3-0</td>
</tr>
</tbody>
</table>
<ScrollModal scrollContainerRef={scrollRef} props={{ctaKey: "om4"}} />

<h3>4 leg options</h3>
<table>
<thead>
<tr>
<th>Bet Builder Examples (Back)</th>
<th>Lay Market</th>
</tr>
</thead>
<tbody>
<tr>
<td>Team A Under 0.5 First Half Goals <span className="and">AND</span> Team B Under 0.5 First Half Goals <span className="and">AND</span> Team A Under 0.5 Second Half Goals <span className="and">AND</span> Team B Under 0.5 Second Half Goals</td>
<td>Correct Score Market: 0-0</td>
</tr>
<tr>
<td>Team A Over 0.5 Goals <span className="and">AND</span> Team A Under 1.5 Goals <span className="and">AND</span> Team B Over 0.5 Goals <span className="and">AND</span> Team B Under 1.5 Goals</td>
<td>Correct Score Market: 1-1</td>
</tr>
<tr>
<td>Win <span className="and">AND</span> Over 0.5 Goals <span className="and">AND</span> Under 1.5 Goals <span className="and">AND</span> BTTS - No</td>
<td>Correct Score Market: 1-0</td>
</tr>
<tr>
<td>Draw <span className="and">AND</span> Over 1.5 Goals <span className="and">AND</span> Under 2.5 Goals <span className="and">AND</span> BTTS</td>
<td>Correct Score Market: 1-1</td>
</tr>
<tr>
<td>Win <span className="and">AND</span> Over 1.5 Goals <span className="and">AND</span> Under 2.5 Goals <span className="and">AND</span> BTTS - No</td>
<td>Correct Score Market: 2-0</td>
</tr>
<tr>
<td>Win <span className="and">AND</span> Over 2.5 Goals <span className="and">AND</span> Under 3.5 Goals <span className="and">AND</span> BTTS</td>
<td>Correct Score Market: 2-1</td>
</tr>
<tr>
<td>Win <span className="and">AND</span> Over 2.5 Goals <span className="and">AND</span> Under 3.5 Goals <span className="and">AND</span> BTTS - No / Team A Exactly 3</td>
<td>Correct Score Market: 3-0</td>
</tr>
</tbody>
</table>
<h3>5 leg options</h3>
<table>
<thead>
<tr>
<th>Bet Builder Examples (Back)</th>
<th>Lay Market</th>
</tr>
</thead>
<tbody>
<tr>
<td>Draw <span className="and">AND</span> Under 0.5 Team A Goals <span className="and">AND</span> Under 0.5 Team B Goals <span className="and">AND</span> BTTS - No <span className="and">AND</span> Correct Score 0-0</td>
<td>Correct Score Market: 0-0</td>
</tr>
<tr>
<td>Draw <span className="and">AND</span> Over 0.5 Team A Goals <span className="and">AND</span> Over 0.5 Team B Goals <span className="and">AND</span> BTTS <span className="and">AND</span> Correct Score 1-1</td>
<td>Correct Score Market: 1-1</td>
</tr>
<tr>
<td>Team to Win <span className="and">AND</span> Over 2.5 Goals <span className="and">AND</span> Under 3.5 Goals <span className="and">AND</span> BTTS <span className="and">AND</span> Correct Score 2-1</td>
<td>Correct Score Market: 2-1</td>
</tr>
<tr>
<td>Team to Win <span className="and">AND</span> Over 2.5 Goals <span className="and">AND</span> Under 3.5 Goals <span className="and">AND</span> Team to Win by Exactly 3 Goals <span className="and">AND</span> BTTS - No</td>
<td>Correct Score Market: 3-0</td>
</tr>
</tbody>
</table>

      <h2>Exchange Markets: <em>Player Anytime Goalscorer</em>, <em>Player First Goalscorer</em>, <em>Player to Score 2+ Goals</em></h2>
      <div className="grid-fixed g2">
        <div className="col">
            <img src={bb4} alt="Lay bet builder on Matchbook" />
        </div>
        <div className="col">
      <h6 className="info">These markets are called <em>Anytime Goalscorer</em>, <em>First Goalscorer</em>, <em>Player to score at least 2 goals</em> on Smarkets and Betdaq, and <em>Player To Score</em>, <em>Player First Goalscorer</em>, <em>Player To Score 2 Goals or More</em> on Betfair.</h6>

     </div>
    </div>

      <table>
        <thead>
          <tr>
            <th>Bet Builder Examples (Back)</th>
            <th>Lay Market</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Player to Score <span className="and">AND</span> Over 0.5 Goals (Total or Team)</td><td>Player Anytime Goalscorer</td></tr>
          <tr><td>Player to Score <span className="and">AND</span> Player 0.5+ Shots on Target</td><td>Player Anytime Goalscorer</td></tr>
          <tr><td>First Goalscorer + Over 0.5 Goals Over 0.5 Goals (Total or Team)</td><td>Player First Goalscorer</td></tr>
          <tr><td>First Goalscorer + Team Goals</td><td>Player First Goalscorer</td></tr>
          <tr><td>Player to Score 2+ Goals + <span className="and">AND</span> Over 1.5 Goals (Total or Team)</td><td>Player to Score 2+ Goals</td></tr>
        </tbody>
      </table>

      <h3>3+ legs</h3>
<table>
  <thead>
    <tr>
      <th>Bet Builder Examples (Back)</th>
      <th>Lay Market</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>3: Player Anytime Goalscorer <span className="and">AND</span> Over 0.5 Goals <span className="and">AND</span> Over 0.5 Team Goals</td>
      <td>Player Anytime Goalscorer</td>
    </tr>
    <tr>
      <td>3: Player First Goalscorer <span className="and">AND</span> Over 0.5 Goals <span className="and">AND</span> Over 0.5 Team Goals</td>
      <td>Player First Goalscorer</td>
    </tr>
    <tr>
      <td>3: Player to Score 2+ Goals <span className="and">AND</span> Over 0.5/1.5 Goals <span className="and">AND</span> Over 0.5/1.5 Team Goals</td>
      <td>Player to Score 2+ Goals</td>
    </tr>
    <tr>
      <td>4: Player Anytime Goalscorer <span className="and">AND</span> Player Over 0.5 Shots on Target <span className="and">AND</span> Over 0.5 Goals <span className="and">AND</span> Over 0.5 Team Goals</td>
      <td>Player Anytime Goalscorer</td>
    </tr>
    <tr>
      <td>4: Player First Goalscorer <span className="and">AND</span> Player Over 0.5 Shots on Target <span className="and">AND</span> Over 0.5 Goals <span className="and">AND</span> Over 0.5 Team Goals</td>
      <td>Player First Goalscorer</td>
    </tr>
    <tr>
      <td>4: Player to Score 2+ Goals <span className="and">AND</span> Player Over 0.5 Shots on Target <span className="and">AND</span> Over 0.5 Goals <span className="and">AND</span> Over 0.5 Team Goals</td>
      <td>Player to Score 2+ Goals</td>
    </tr>
  </tbody>
</table>

<h2>Laying on the Exchange Market <em>Match Result</em></h2>
<p>Finally, we might be able to use our main match result markets to lay a small number of bet builders.</p>
<table>
        <thead>
          <tr>
            <th>Bet Builder Examples (Back)</th>
            <th>Lay Market</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>2: Win <span className="and">AND</span> Over 0.5 Goals or Team Goals</td><td>Match Result</td></tr>
          <tr><td>3: Team to Win <span className="and">AND</span> Over 0.5 Goals <span className="and">AND</span> Over 0.5 Team Goals	</td><td>Player Anytime Goalscorer</td></tr>
        </tbody>
      </table>

      <h2>Key takeaways</h2>
      <p>
      Bet builders can't be layed directly. You cannot build the matching bet at an exchange, and lay that like an accumulator. 
      </p>
      <p>
      So we create a bet builder which is equivalent to a market that exists on exchanges.
      </p>
      <p>
        We can then calculate our lay stake as normal.
      </p>
    </div>
  );
};

export default LayBetBuildersGuide;
